import { ReportMetadata } from '../types';
import { isProjectFinished } from './projectBilling';
import type { ListProject } from './listProject';

// Reine Logik rund um Projektbeschreibung und Projektbilder — ohne DOM, ohne
// Supabase. Die IO-Seite (Tabelle `project_media`, Bucket) liegt in
// projectMediaService.ts, Download/ZIP/PDF-Verkleinerung in
// projectReferenceImage.ts, die Oberflaechen in ProjectReferenceCard.tsx,
// AdminReferenceSheetView.tsx und AdminProjectImagesView.tsx.
//
// Anlass (Betreiber 12.09.2026): mehr Werbung in sozialen Medien. Dafuer
// braucht jedes Projekt eine kurze Beschreibung (bleibt im JSON,
// `metadata.projectDescription`) und hochaufloesende Bilder (Tabelle
// `project_media`, Nr. 99), die der Innendienst ohne Umweg herunterladen kann.

/** Obergrenze je Datei. Supabase-Projektlimit ist meist 50 MB — darueber
 *  bricht der Upload serverseitig mit „Payload too large" ab. */
export const REFERENCE_IMAGE_MAX_BYTES = 50 * 1024 * 1024;

/** Nur Formate, die JEDER Browser dekodiert (HEIC kann Safari, Chrome nicht)
 *  und die Social-Media-Plattformen annehmen. Spiegel des CHECKs in Nr. 99. */
export const REFERENCE_IMAGE_TYPES: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
};

/** Ab dieser Kantenlaenge gilt ein Bild als Social-Media-tauglich (Instagram-
 *  Feed 1080 px, LinkedIn/Facebook 1200 px; 1920 laesst Spielraum fuer Zuschnitt). */
export const REFERENCE_IMAGE_GOOD_WIDTH = 1920;
export const REFERENCE_IMAGE_MIN_WIDTH = 1080;

/** Bildunterschrift: Spiegel des CHECKs in Nr. 99. */
export const CAPTION_MAX = 500;

/**
 * Eine Zeile aus `project_media` (Nr. 99), camelCase. `url` ist die
 * oeffentliche Bucket-URL, vom Service aus `storagePath` gebaut.
 */
export interface ProjectMedia {
    id: string;
    projectId: string;
    storagePath: string;
    url: string;
    fileName: string;
    mimeType: string;
    sizeBytes: number;
    width: number | null;
    height: number | null;
    titelbild: boolean;
    sortOrder: number;
    caption: string | null;
    uploadedBy: string | null;
    uploadedById: string | null;
    createdAt: string;
    updatedAt: string;
}

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const strOrNull = (v: unknown): string | null => (v == null || v === '' ? null : str(v));
const num = (v: unknown, fallback: number): number => {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : fallback;
};
const numOrNull = (v: unknown): number | null => (v == null ? null : num(v, 0));

/** snake_case-Zeile → ProjectMedia. `urlFor` baut die oeffentliche URL. */
export const mapProjectMediaRow = (row: Record<string, unknown>, urlFor: (path: string) => string): ProjectMedia => {
    const storagePath = str(row.storage_pfad);
    return {
        id: str(row.id),
        projectId: str(row.project_id),
        storagePath,
        url: urlFor(storagePath),
        fileName: str(row.dateiname),
        mimeType: str(row.mime_type),
        sizeBytes: num(row.groesse_bytes, 0),
        width: numOrNull(row.breite),
        height: numOrNull(row.hoehe),
        titelbild: row.titelbild === true,
        sortOrder: num(row.sort_order, 0),
        caption: strOrNull(row.bildunterschrift),
        uploadedBy: strOrNull(row.hochgeladen_von),
        uploadedById: strOrNull(row.hochgeladen_von_id),
        createdAt: str(row.created_at),
        updatedAt: str(row.updated_at),
    };
};

/** Galerie-Reihenfolge: Titelbild zuerst, dann sort_order, dann Upload-Zeit. */
export const sortProjectMedia = (items: ProjectMedia[]): ProjectMedia[] =>
    [...items].sort((a, b) => {
        if (a.titelbild !== b.titelbild) return a.titelbild ? -1 : 1;
        if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
        return a.createdAt.localeCompare(b.createdAt);
    });

/** Das Bild fuers Referenzblatt: Titelbild, sonst das erste der Galerie. */
export const titelbildVon = (items: ProjectMedia[]): ProjectMedia | null => {
    if (!items || items.length === 0) return null;
    return sortProjectMedia(items)[0];
};

/** Zeilen aus `project_media` nach Projekt gruppieren (jede Gruppe sortiert). */
export const groupProjectMedia = (items: ProjectMedia[]): Map<string, ProjectMedia[]> => {
    const m = new Map<string, ProjectMedia[]>();
    for (const it of items) {
        const list = m.get(it.projectId) || [];
        list.push(it);
        m.set(it.projectId, list);
    }
    for (const [k, v] of m) m.set(k, sortProjectMedia(v));
    return m;
};

/** Naechster sort_order fuer einen Upload: max + 1, bei leerer Galerie 0. */
export const nextSortOrder = (items: ProjectMedia[]): number =>
    items.length === 0 ? 0 : Math.max(...items.map(i => i.sortOrder)) + 1;

export interface FileLike {
    name: string;
    type: string;
    size: number;
}

/** Prueft eine Datei VOR dem Upload. Liefert den Fehlertext oder null. */
export const validateReferenceImageFile = (file: FileLike): string | null => {
    if (!REFERENCE_IMAGE_TYPES[file.type]) {
        return 'Bitte ein JPG, PNG oder WebP hochladen. Andere Formate (HEIC, TIFF, PDF) kann der Browser nicht anzeigen.';
    }
    if (file.size <= 0) return 'Die Datei ist leer.';
    if (file.size > REFERENCE_IMAGE_MAX_BYTES) {
        return `Die Datei ist ${describeFileSize(file.size)} groß — erlaubt sind höchstens ${describeFileSize(REFERENCE_IMAGE_MAX_BYTES)}.`;
    }
    return null;
};

/** Dateiendung fuer den Objektpfad aus dem MIME-Typ. */
export const referenceImageExtension = (mimeType: string): string => REFERENCE_IMAGE_TYPES[mimeType] || 'jpg';

/**
 * Objektpfad im Bucket. Erster Abschnitt = Projekt-UUID (Pfadschema aus
 * policy_inventory.sql 5d; der CHECK `project_media_storage_pfad_check`
 * verlangt genau dieses Praefix), zweiter = fester Unterordner, Dateiname =
 * zufaellig (nicht erratbar, kein Ueberschreiben durch gleichen Namen).
 */
export const referenceImageStoragePath = (projectId: string, mimeType: string, token: string): string =>
    `${projectId}/referenz/${token}.${referenceImageExtension(mimeType)}`;

/**
 * Dateiname fuer den Download: Projektnummer_Projektname[_n].ext,
 * Windows-sicher (Umlaute ersetzt, Sonderzeichen zu Unterstrich).
 */
export const referenceImageDownloadName = (
    projectNumber: string | undefined,
    projectName: string | undefined,
    mimeType: string,
    index?: number,
): string => {
    const raw = [projectNumber, projectName].filter(Boolean).join('_') || 'Projektbild';
    const safe = raw
        .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
        .replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue')
        .replace(/[^A-Za-z0-9._-]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
        .slice(0, 100);
    const suffix = index !== undefined ? `_${index}` : '';
    return `${safe || 'Projektbild'}${suffix}.${referenceImageExtension(mimeType)}`;
};

export const describeFileSize = (bytes: number): string => {
    if (!Number.isFinite(bytes) || bytes < 0) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
};

export type ResolutionVerdict = 'gut' | 'knapp' | 'klein' | 'unbekannt';

/** Aufloesungs-Urteil fuer die Oberflaeche; misst an der laengeren Kante. */
export const resolutionVerdict = (width?: number | null, height?: number | null): ResolutionVerdict => {
    if (!width || !height) return 'unbekannt';
    const longEdge = Math.max(width, height);
    if (longEdge >= REFERENCE_IMAGE_GOOD_WIDTH) return 'gut';
    if (longEdge >= REFERENCE_IMAGE_MIN_WIDTH) return 'knapp';
    return 'klein';
};

export const resolutionVerdictText = (verdict: ResolutionVerdict): string => {
    switch (verdict) {
        case 'gut': return 'Hochauflösend — für Social Media und Druck geeignet';
        case 'knapp': return 'Ausreichend für Social Media, für Druck knapp';
        case 'klein': return 'Zu klein — bitte ein Bild mit mindestens 1920 px langer Kante hochladen';
        default: return 'Auflösung unbekannt';
    }
};

const formatDay = (iso?: string): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/** „01.03.2026 – 30.09.2026", „ab 01.03.2026", „bis …" oder „-". */
export const formatProjectPeriod = (startDate?: string, endDate?: string): string => {
    const s = formatDay(startDate);
    const e = formatDay(endDate);
    if (s && e) return `${s} – ${e}`;
    if (s) return `ab ${s}`;
    if (e) return `bis ${e}`;
    return '-';
};

export interface ProjectImageRow {
    id: string;
    projectNumber: string;
    name: string;
    /** Auftraggeber (customerName), Rueckfall Bauherr (clientName). */
    customer: string;
    client: string;
    coordinator: string;
    category: string;
    startDate: string;
    endDate: string;
    period: string;
    isFinished: boolean;
    description: string;
    /** Galerie in Anzeige-Reihenfolge (Titelbild zuerst). */
    images: ProjectMedia[];
    /** Titelbild oder erstes Bild; null ohne Bilder. */
    titelbild: ProjectMedia | null;
}

/**
 * Was buildProjectImageRows an einer Projektzeile liest: die Spalten als
 * Ausschnitt aus ListProject, `data` darf fehlen (Zeilen ohne Metadaten).
 */
export type ProjectImageSource = Pick<ListProject, 'id'> &
    Partial<Pick<ListProject, 'name' | 'project_number' | 'coordinator_name' | 'category' | 'status'>> & {
        data?: { metadata?: Partial<ReportMetadata> | null } | null;
    };

/**
 * Zeilen fuer die Admin-Uebersicht „Projektbilder" aus den rohen
 * projects-Zeilen (Spalten + data.metadata) und den Bildern je Projekt.
 * Dieselbe Feldableitung wie das Referenzblatt
 * (AdminReferenceSheetView.processedProjects), damit beide Ansichten
 * dasselbe Projekt gleich benennen.
 */
export const buildProjectImageRows = (projects: readonly ProjectImageSource[], mediaByProject: Map<string, ProjectMedia[]>): ProjectImageRow[] => {
    return (projects || []).map(p => {
        const meta = (p?.data?.metadata || {}) as Partial<ReportMetadata>;
        const gd = meta.globalData;
        const startDate = gd?.startDate || '';
        const endDate = gd?.endDate || '';
        const images = sortProjectMedia(mediaByProject.get(p.id) || []);
        return {
            id: p.id,
            projectNumber: p.project_number || meta.projectNumber || '',
            name: p.name || meta.projectName || '',
            customer: meta.customerName || meta.clientName || '',
            client: meta.clientName || '',
            coordinator: p.coordinator_name || meta.coordinator || '',
            category: p.category || meta.projectCategory || meta.category || '',
            startDate,
            endDate,
            period: formatProjectPeriod(startDate, endDate),
            isFinished: isProjectFinished(p),
            description: (meta.projectDescription || '').trim(),
            images,
            titelbild: images[0] || null,
        };
    });
};

export type ProjectImageFilter = 'with-image' | 'without-image' | 'all';

const newestUpload = (r: ProjectImageRow): string =>
    r.images.reduce((max, i) => (i.createdAt > max ? i.createdAt : max), '');

/** Suche + Bildfilter fuer die Uebersicht; sortiert Projekte MIT Bild nach vorn,
 *  darin die mit dem juengsten Upload zuerst. */
export const filterProjectImageRows = (rows: ProjectImageRow[], search: string, filter: ProjectImageFilter): ProjectImageRow[] => {
    const q = search.trim().toLowerCase();
    const out = rows.filter(r => {
        const hat = r.images.length > 0;
        if (filter === 'with-image' && !hat) return false;
        if (filter === 'without-image' && hat) return false;
        if (!q) return true;
        return [r.name, r.projectNumber, r.customer, r.client, r.coordinator, r.description, ...r.images.map(i => i.caption || '')]
            .some(v => (v || '').toLowerCase().includes(q));
    });
    return out.sort((a, b) => {
        const ha = a.images.length > 0;
        const hb = b.images.length > 0;
        if (ha !== hb) return ha ? -1 : 1;
        if (ha && hb) {
            const ta = newestUpload(a);
            const tb = newestUpload(b);
            if (ta !== tb) return ta < tb ? 1 : -1;
        }
        return (a.projectNumber || '').localeCompare(b.projectNumber || '', 'de');
    });
};

/**
 * Bildmasse fuer das PDF: Bild in eine Box (mm) einpassen, Seitenverhaeltnis
 * bleibt. Liefert null, wenn die Masse fehlen.
 */
export const fitImageIntoBox = (
    width: number | null | undefined,
    height: number | null | undefined,
    boxWidth: number,
    boxHeight: number,
): { w: number; h: number } | null => {
    if (!width || !height || width <= 0 || height <= 0) return null;
    const scale = Math.min(boxWidth / width, boxHeight / height);
    return { w: Math.round(width * scale * 100) / 100, h: Math.round(height * scale * 100) / 100 };
};
